import { DatadogDevtools, DatadogProvider } from '@datadog/openfeature-browser';
import {
  OpenFeature,
  OpenFeatureEventEmitter,
  StandardResolutionReasons,
} from '@openfeature/web-sdk';
import type { Provider } from '@openfeature/web-sdk';
import { setProviders } from './providerState';
import { classifyFailure, reportFlagInitFailure } from './transport';
import type { FlagsConfig, FlagTransportRoute } from './types';

/** Datadog org region when the host doesn't specify one (US1). */
const DEFAULT_SITE = 'datadoghq.com';

/**
 * A credential is usable only if it's non-empty and not an unresolved templating
 * placeholder. Hosts that read config from a secrets bundle can leave a literal
 * `<loaded from secrets>` string in place when a key is missing from the env — the
 * `<` check catches that rather than shipping it to Datadog as a token.
 */
const isUsableCred = (value: string) => !!value && !value.includes('<');

/**
 * Inner provider used when Datadog creds are absent (devbox, CI, most test envs):
 * resolves everything to the caller's default. `DatadogDevtools` still wraps it, so
 * local overrides work in creds-less envs (e.g. Playwright forcing a flag). Carries
 * its own event emitter so override changes can be broadcast for live re-evaluation.
 */
const noopProvider: Provider = {
  metadata: { name: 'no-op' },
  events: new OpenFeatureEventEmitter(),
  resolveBooleanEvaluation: (_key, defaultValue) => ({
    value: defaultValue,
    reason: StandardResolutionReasons.DEFAULT,
  }),
  resolveStringEvaluation: (_key, defaultValue) => ({
    value: defaultValue,
    reason: StandardResolutionReasons.DEFAULT,
  }),
  resolveNumberEvaluation: (_key, defaultValue) => ({
    value: defaultValue,
    reason: StandardResolutionReasons.DEFAULT,
  }),
  resolveObjectEvaluation: (_key, defaultValue) => ({
    value: defaultValue,
    reason: StandardResolutionReasons.DEFAULT,
  }),
};

let activeProvider: DatadogProvider | null = null;

/**
 * Registers the Datadog Feature Flags provider on OpenFeature — the single place a
 * host wires up flags. Call once at an app root, before mounting
 * `<OpenFeatureProvider>`; components then read flags via the `use*FlagValue` hooks.
 *
 * @remarks
 * Always installs {@link DatadogDevtools} so local overrides work everywhere. With
 * usable creds it wraps a real `DatadogProvider` (fetches config from the endpoint);
 * without them it wraps a no-op provider so flags fall back to defaults rather than
 * shipping a bogus token that Datadog 401s on. Per-environment on/off is a Datadog
 * targeting rule against `env` — this package never compares it.
 *
 * Whitelisting: the SDK is network-first, so hosts on networks that block
 * `datadoghq.com` pass a `proxyOrigin` and the config fetch is routed through
 * `<origin>/_datadog-flags` instead. Exposure/eval/RUM tracking is off, so that
 * config fetch is the only browser→Datadog request.
 *
 * Stays synchronous — the boot fetch is awaited in the background.
 */
export const initFlags = (config: FlagsConfig): void => {
  const { clientToken, applicationId, env, onError } = config;

  let inner: Provider;
  let route: FlagTransportRoute | null = null;
  if (isUsableCred(clientToken) && isUsableCred(applicationId)) {
    const proxyOrigin = (config.proxyOrigin ?? '').replace(/\/$/, '');
    const flaggingProxy = proxyOrigin
      ? `${proxyOrigin}/_datadog-flags/precompute-assignments`
      : undefined;

    route = flaggingProxy ? 'proxy' : 'direct-to-datadog';

    activeProvider = new DatadogProvider({
      clientToken,
      applicationId,
      site: config.site ?? DEFAULT_SITE,
      env,
      ...(flaggingProxy ? { flaggingProxy } : {}),
      enableExposureLogging: false,
      enableFlagEvaluationTracking: false,
      enableRumFeatureFlagTracking: false,
    });
    inner = activeProvider;
  } else {
    activeProvider = null;
    inner = noopProvider;
  }

  const devtoolsProvider = new DatadogDevtools(inner);
  setProviders(activeProvider, devtoolsProvider);

  // No route means the no-op provider: no fetch to fail, so nothing to report.
  if (route) {
    void ensureFlagsLoaded(devtoolsProvider, route, onError);
  } else {
    OpenFeature.setProvider(devtoolsProvider);
  }
};

/**
 * Backoff between boot config fetch retries. The SDK has none of its own, so without
 * this one flaky fetch pins every flag to its read-site default for the session.
 */
const RETRY_DELAYS_MS = [1000, 4000];

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

/**
 * Awaits the boot config fetch, retrying before it reports.
 *
 * `setProviderAndWait` rather than `setProvider`: same code path, but `setProvider`
 * swallows the rejection into `console.error`, which a console-capturing error
 * reporter then turns into a contextless event. Retries use the provider's
 * `onContextChange` rather than `OpenFeature.setContext` for the same reason.
 */
async function ensureFlagsLoaded(
  provider: DatadogDevtools,
  route: FlagTransportRoute,
  onError?: FlagsConfig['onError'],
): Promise<void> {
  const startedAt = Date.now();
  // Counted, not derived from the budget, so it holds when the loop exits early.
  let attempts = 1;
  let lastError: unknown;

  try {
    await OpenFeature.setProviderAndWait(provider);
    return;
  } catch (error) {
    lastError = error;
  }

  for (const delay of RETRY_DELAYS_MS) {
    // An abort means the SDK superseded its own request; nothing to recover.
    if (classifyFailure(lastError).kind === 'aborted') break;

    await sleep(delay);
    attempts += 1;
    try {
      const context = OpenFeature.getContext();
      await activeProvider?.onContextChange(context, context);
      return;
    } catch (error) {
      lastError = error;
    }
  }

  reportFlagInitFailure({
    route,
    error: lastError,
    attempts,
    elapsedMs: Date.now() - startedAt,
    onError,
  });
}
